// Notifications dropdown — visually paired with the AppBar search panel:
// one bordered panel with a header (title + unread pill + mark-all-read),
// slim tabs, flat severity-tinted rows with relative timestamps, skeleton
// loading, and a retryable error state. Unread rows carry a dot; everything
// is still marked read when the panel closes (that's what clears the badge).
import React, { useState, useEffect, useContext } from 'react';
import API_BASE_URL from '../config/api';
import {
  IconButton,
  Badge,
  Popper,
  Grow,
  Box,
  Stack,
  Button,
  Chip,
  Tabs,
  Tab,
  Skeleton,
  ClickAwayListener,
  Paper,
  Tooltip,
  useTheme,
  Typography,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { NotificationContext } from '../context/NotificationContext';
import { formatDateTime, formatDate } from '../utils/dateUtils';

// same chrome as the search panel so the AppBar popups feel like one family
const DropdownPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 14,
  width: 400,
  maxWidth: 'calc(100vw - 24px)',
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
  background: theme.palette.mode === 'dark'
    ? 'rgba(28, 29, 32, 0.98)'
    : 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(10px)',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 16px 48px rgba(0, 0, 0, 0.6)'
    : '0 16px 48px rgba(15, 23, 42, 0.16)',
}));

const NotificationArea = styled(Box)(({ theme }) => ({
  maxHeight: 'min(52vh, 460px)',
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  padding: '4px 0 8px',
  '&::-webkit-scrollbar': { width: 6 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.18)',
    borderRadius: 3,
  },
}));

const NotificationRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  borderRadius: 10,
  margin: '1px 8px',
  padding: '8px 10px',
  transition: 'background-color 120ms ease',
  '&:hover': { backgroundColor: theme.palette.action.hover },
}));

// server timestamps are UTC but may lack the 'Z' suffix (same convention as
// utils/dateUtils.parseServerDate, which isn't exported)
const parseServerDate = (date) => {
  if (!date) return null;
  if (date instanceof Date) return date;
  const dateStr = String(date);
  if (!dateStr.endsWith('Z') && !dateStr.match(/[+-]\d{2}:\d{2}$/)) return new Date(`${dateStr}Z`);
  return new Date(dateStr);
};
const timeAgo = (ts) => {
  const d = parseServerDate(ts);
  if (!d || Number.isNaN(d.getTime())) return '';
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return formatDate(ts);
};

const NotificationDropdown = () => {
  const { notifications, setNotifications, unreadCount } = useContext(NotificationContext);
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const open = Boolean(anchorEl);
  const hasUnreadErrors = notifications.some((n) => !n.IsRead && n.IsError);
  const adminNotifications = notifications.filter((n) => n.NotificationType === 1);
  const processNotifications = notifications.filter((n) => n.NotificationType === 0);

  const fetchNotifications = async () => {
    const token = localStorage.getItem('jwtToken');
    if (!token) return;

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/Notification`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch notifications');

      const data = await response.json();
      const newNotifications = Array.isArray(data) ? data : data.Data || [];
      const normalized = newNotifications.map((notif) => ({
        Id: notif.id,
        UserId: notif.userId,
        Message: notif.message,
        IsError: notif.isError,
        NotificationType: notif.notificationType,
        Timestamp: notif.timestamp,
        IsRead: notif.isRead,
      }));

      setNotifications(normalized);
    } catch (err) {
      setError(err.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const markAllAsRead = async () => {
    const token = localStorage.getItem('jwtToken');
    if (!token) return;
    if (!notifications.some((n) => !n.IsRead)) return;   // nothing to mark

    try {
      const res = await fetch(`${API_BASE_URL}/api/Notification/markread`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      if (!res.ok) throw new Error('Failed to mark notifications as read');

      setNotifications((prev) => prev.map((n) => ({ ...n, IsRead: true })));
    } catch (e) {
      console.error('markAllAsRead →', e);
    }
  };

  const handleClick = async (event) => {
    const isOpening = !anchorEl;
    setAnchorEl(isOpening ? event.currentTarget : null);

    if (isOpening) {
      await fetchNotifications();
    }
  };

  const handleClose = async () => {
    setAnchorEl(null);
    setError(null);
    await markAllAsRead();
  };

  const handleTabChange = (event, newValue) => setTabValue(newValue);

  const severityOf = (notif) => {
    if (notif.IsError) return { icon: ErrorIcon, color: theme.palette.error.main };
    if (notif.NotificationType === 1) return { icon: InfoIcon, color: theme.palette.info.main };
    return { icon: CheckCircleIcon, color: theme.palette.success.main };
  };

  const renderNotificationList = (notifs, noMessage) => (
    notifs.length === 0 ? (
      <Box sx={{ py: 4, px: 3, textAlign: 'center' }}>
        <Box sx={{ color: 'text.disabled', mb: 1 }}><NotificationsNoneIcon sx={{ fontSize: 30 }} /></Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>All caught up</Typography>
        <Typography variant="caption" color="text.secondary">{noMessage}</Typography>
      </Box>
    ) : (
      notifs.map((notif) => {
        const sev = severityOf(notif);
        const SevIcon = sev.icon;
        return (
          <NotificationRow key={notif.Id}>
            <Box
              sx={{
                width: 30, height: 30, borderRadius: 1.25, flexShrink: 0, mt: 0.25,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: alpha(sev.color, theme.palette.mode === 'dark' ? 0.18 : 0.1),
                color: sev.color,
              }}
            >
              <SevIcon sx={{ fontSize: 17 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: notif.IsRead ? 400 : 600,
                  color: notif.IsRead ? 'text.secondary' : 'text.primary',
                  wordBreak: 'break-word',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
                title={notif.Message}
              >
                {notif.Message}
              </Typography>
              <Tooltip title={formatDateTime(notif.Timestamp)} placement="bottom-start">
                <Typography variant="caption" color="text.secondary" component="span">
                  {timeAgo(notif.Timestamp)}
                </Typography>
              </Tooltip>
            </Box>
            {!notif.IsRead && (
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: sev.color, flexShrink: 0, mt: 1 }} />
            )}
          </NotificationRow>
        );
      })
    )
  );

  const loadingSkeleton = (
    <Box sx={{ px: 2, py: 1 }}>
      {[0, 1, 2].map((i) => (
        <Stack key={i} direction="row" spacing={1.25} alignItems="center" sx={{ py: 1 }}>
          <Skeleton variant="rounded" width={30} height={30} />
          <Box sx={{ flex: 1 }}>
            <Skeleton width="85%" height={18} />
            <Skeleton width="30%" height={14} />
          </Box>
        </Stack>
      ))}
    </Box>
  );

  return (
    <Box component="span">
      <Tooltip title={open ? '' : 'Notifications'}>
        <IconButton
          onClick={handleClick}
          sx={{
            color: theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.text.primary,
            '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' },
            ...(open && { bgcolor: 'primary.main', color: 'primary.contrastText' }),
          }}
        >
          <Badge
            badgeContent={unreadCount}
            color={hasUnreadErrors ? 'error' : 'primary'}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.65rem',
                minWidth: 18,
                height: 18,
                padding: '0 4px',
                top: 2,
                right: 2,
                border: `2px solid ${theme.palette.background.default}`,
              },
            }}
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        transition
        disablePortal
        sx={{ zIndex: theme.zIndex.modal + 1 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} style={{ transformOrigin: 'top right' }}>
            <Box sx={{ pt: 1 }}>
              <ClickAwayListener onClickAway={handleClose} touchEvent={false}>
                <DropdownPaper>
                  {/* header */}
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.25 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, flex: 1 }}>
                      Notifications
                    </Typography>
                    {unreadCount > 0 && (
                      <Chip
                        size="small"
                        label={`${unreadCount} new`}
                        sx={{
                          height: 20, fontSize: 11, fontWeight: 700,
                          bgcolor: alpha(hasUnreadErrors ? theme.palette.error.main : theme.palette.primary.main, 0.12),
                          color: hasUnreadErrors ? 'error.main' : 'primary.main',
                        }}
                      />
                    )}
                    {unreadCount > 0 && (
                      <Tooltip title="Mark all as read">
                        <Button size="small" onClick={markAllAsRead} startIcon={<DoneAllIcon sx={{ fontSize: 15 }} />}
                          sx={{ minWidth: 0, fontSize: 12, textTransform: 'none', px: 1 }}>
                          Mark read
                        </Button>
                      </Tooltip>
                    )}
                  </Stack>

                  {/* tabs */}
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label="notification tabs"
                    variant="fullWidth"
                    sx={{
                      minHeight: 38,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      '& .MuiTab-root': {
                        minHeight: 38,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.82rem',
                        color: theme.palette.text.secondary,
                        '&.Mui-selected': { color: theme.palette.primary.main },
                      },
                      '& .MuiTabs-indicator': { height: 2.5 },
                    }}
                  >
                    <Tab label={`All · ${notifications.length}`} />
                    <Tab label={`Admin · ${adminNotifications.length}`} />
                    <Tab label={`Processes · ${processNotifications.length}`} />
                  </Tabs>

                  {/* body */}
                  {loading && loadingSkeleton}
                  {!loading && error && (
                    <Box sx={{ py: 3, px: 3, textAlign: 'center' }}>
                      <Typography variant="body2" color="error" sx={{ mb: 1 }}>{error}</Typography>
                      <Button size="small" variant="outlined" onClick={fetchNotifications}>Retry</Button>
                    </Box>
                  )}
                  {!loading && !error && (
                    <NotificationArea>
                      {tabValue === 0 && renderNotificationList(notifications, 'No notifications right now')}
                      {tabValue === 1 && renderNotificationList(adminNotifications, 'No admin notifications')}
                      {tabValue === 2 && renderNotificationList(processNotifications, 'No process notifications')}
                    </NotificationArea>
                  )}
                </DropdownPaper>
              </ClickAwayListener>
            </Box>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};

export default NotificationDropdown;
